'use client';

import {
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';

import Link from 'next/link';

import { StyleOverrides } from '@/types/richTextTypes';

import getPrimaryButtonProps, {
  Functionality,
} from './PrimaryButtonFunctionality';

type RichTextHeading = {
  raw?: string;
  json?: unknown;
};

type TextContentHeading = {
  content: ReactNode;
};

type JsonButtonData = {
  content?: string;
  link?: string;
  styles?: StyleOverrides & {
    button?: string;
  };
};

export type PrimaryButtonData = JsonButtonData & {
  title?: string;
  slug?: string;
  buttonType?: 'primary' | 'secondary' | string;
  heading?: string | RichTextHeading | TextContentHeading;
  description?: RichTextHeading;
  functionality?: Functionality | string;
  isLink?: boolean;
  formId?: string;
};

type ButtonClickEvent = MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

type ButtonProps = {
  data: PrimaryButtonData;
  className?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
  widthFitContent?: boolean;
  paddingClassName?: string;
  widthClassName?: string;
  onClick?: (event: ButtonClickEvent) => void;
  onShowForm?: () => void;
  onEnquireNow?: () => void;
  ariaLabel?: string;
  role?: string;
};

const DEFAULT_BUTTON_DATA: PrimaryButtonData = {
  title: '',
  slug: '',
};

const BASE_STYLES =
  'items-center justify-center rounded-lg border-2 font-mulish text-base font-normal! leading-5 tracking-normal transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white cursor-pointer';

const DEFAULT_DISPLAY_STYLES = 'inline-flex';

// Hide booking CTAs on mobile; keep them in hero / floating-actions sections.
// `!` so parent className values like `flex` cannot override the hide.
const MOBILE_HIDDEN_BOOKING_STYLES =
  'hidden! md:inline-flex! [[data-testid*="hero"]_&]:inline-flex! [[id*="hero"]_&]:inline-flex! [[data-hide-floating-actions="true"]_&]:inline-flex!';

const BOOK_APPOINTMENT_PATH = '/book-appointment';
const BOOKING_CALENDAR_HREF = '#booking-calendar-widget';

const PRIMARY_STYLES =
  'bg-theme-primary text-white border-transparent hover:bg-transparent hover:text-theme-primary hover:border-theme-primary font-normal cursor-pointer';

const SECONDARY_STYLES =
  'bg-transparent text-theme-primary border-theme-primary hover:bg-theme-primary hover:text-white hover:border-theme-primary font-normal cursor-pointer';

const DISABLED_STYLES =
  'disabled:bg-theme-primary-light disabled:text-text-default disabled:cursor-not-allowed disabled:opacity-100';

const DEFAULT_PADDING_STYLES = 'px-[40px] py-[15px]';
const FIT_CONTENT_PADDING_STYLES = 'px-p-lg py-[10px]';
const FIT_CONTENT_STYLES = 'w-fit text-center';

// eslint-disable-next-line security/detect-unsafe-regex -- False positive: simple optional group, no nested quantifiers
const EXTERNAL_URL_PATTERN = /^(https?:)?\/\//i;
const UNSAFE_URL_PATTERN = /^\s*javascript:/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeHref = (href?: string): string | undefined => {
  const trimmedHref = href?.trim();

  if (!trimmedHref || UNSAFE_URL_PATTERN.test(trimmedHref)) {
    return undefined;
  }

  return trimmedHref;
};

const isExternalWebHref = (href: string): boolean =>
  EXTERNAL_URL_PATTERN.test(href);

const isTelHref = (href: string): boolean => href.startsWith('tel:');

const isMailHref = (href: string): boolean => href.startsWith('mailto:');

const isHashHref = (href: string): boolean => href.startsWith('#');

const isBookAppointmentPath = (value?: string): boolean =>
  normalizeHref(value) === BOOK_APPOINTMENT_PATH;

const isBookingCalendarHref = (value?: string): boolean =>
  normalizeHref(value) === BOOKING_CALENDAR_HREF;

const shouldHideOnMobile = (
  data: PrimaryButtonData,
  resolvedHref?: string,
): boolean =>
  data.functionality === 'book_appointment' ||
  isBookAppointmentPath(data.link) ||
  isBookAppointmentPath(data.slug) ||
  isBookingCalendarHref(data.link) ||
  isBookingCalendarHref(data.slug) ||
  isBookingCalendarHref(resolvedHref);

const getTextLabel = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || undefined;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return undefined;
};

const renderTitle = (data: PrimaryButtonData): ReactNode => {
  const heading = data.heading;

  if (heading) {
    if (typeof heading === 'string') {
      return heading;
    }

    if (isRecord(heading) && 'content' in heading) {
      return heading.content as ReactNode;
    }
  }

  return data.content || data.title || '';
};

const getFallbackAriaLabel = (data: PrimaryButtonData): string | undefined => {
  const heading = data.heading;

  if (heading) {
    if (typeof heading === 'string') {
      return getTextLabel(heading);
    }

    if (isRecord(heading) && 'content' in heading) {
      return getTextLabel(heading.content);
    }
  }

  return getTextLabel(data.content) ?? getTextLabel(data.title);
};

function PrimaryButtonComponent({
  data = DEFAULT_BUTTON_DATA,
  className = '',
  type = 'button',
  disabled = false,
  widthFitContent = false,
  paddingClassName,
  widthClassName,
  onClick,
  onShowForm,
  onEnquireNow,
  ariaLabel,
  role,
}: ButtonProps) {
  const { href: generatedHref, onClick: functionalityOnClick } = useMemo(
    () =>
      getPrimaryButtonProps({
        functionality: data.functionality,
        slug: data.slug,
        onShowForm,
        onEnquireNow,
      }),
    [data.functionality, data.slug, onShowForm, onEnquireNow],
  );

  const href = useMemo(() => {
    const fallbackHref =
      data.link ??
      (data.functionality === 'custom' || !data.functionality
        ? data.slug
        : undefined);

    return normalizeHref(generatedHref ?? fallbackHref);
  }, [data.functionality, data.link, data.slug, generatedHref]);

  const styles = useMemo(
    () =>
      [
        BASE_STYLES,
        data.buttonType === 'secondary' ? SECONDARY_STYLES : PRIMARY_STYLES,
        paddingClassName ??
          (widthFitContent
            ? FIT_CONTENT_PADDING_STYLES
            : DEFAULT_PADDING_STYLES),
        widthFitContent ? FIT_CONTENT_STYLES : '',
        widthClassName,
        disabled ? DISABLED_STYLES : '',
        data.styles?.button,
        className,
        shouldHideOnMobile(data, href)
          ? MOBILE_HIDDEN_BOOKING_STYLES
          : DEFAULT_DISPLAY_STYLES,
        'font-normal!',
      ]
        .filter(Boolean)
        .join(' '),
    [
      className,
      data,
      disabled,
      href,
      paddingClassName,
      widthClassName,
      widthFitContent,
    ],
  );

  const handleClick = useCallback(
    (event: ButtonClickEvent) => {
      if (disabled) {
        event.preventDefault();
        return;
      }

      onClick?.(event);

      if (event.defaultPrevented) {
        return;
      }

      functionalityOnClick?.(event);
    },
    [disabled, functionalityOnClick, onClick],
  );

  const title = renderTitle(data);
  const computedAriaLabel = ariaLabel ?? getFallbackAriaLabel(data);

  if (!href || disabled || data.isLink === false || type === 'submit') {
    return (
      <button
        type={type}
        className={styles}
        disabled={disabled}
        onClick={handleClick}
        aria-label={computedAriaLabel}
        role={role}
      >
        {title}
      </button>
    );
  }

  const shouldUseNativeAnchor =
    isExternalWebHref(href) ||
    isTelHref(href) ||
    isMailHref(href) ||
    isHashHref(href) ||
    data.functionality === 'external_link';

  if (shouldUseNativeAnchor) {
    const shouldOpenInNewTab =
      !isTelHref(href) &&
      !isMailHref(href) &&
      !isHashHref(href) &&
      (isExternalWebHref(href) || data.functionality === 'external_link');

    return (
      <a
        href={href}
        className={styles}
        onClick={handleClick}
        aria-label={computedAriaLabel}
        role={role}
        target={shouldOpenInNewTab ? '_blank' : undefined}
        rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
      >
        {title}
      </a>
    );
  }

  return (
    <Link
      href={href}
      className={styles}
      onClick={handleClick}
      aria-label={computedAriaLabel}
      role={role}
    >
      {title}
    </Link>
  );
}

const PrimaryButton = memo(PrimaryButtonComponent);

PrimaryButton.displayName = 'PrimaryButton';

export default PrimaryButton;
